'use client'

import { AnimatePresence, motion, useWillChange } from 'motion/react'
import { useLocale } from 'next-intl'
import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useMeasure } from 'react-use'
import { Input } from '@/components/input/input'
import { BecomeAPartnerSearchPlaceholder } from '@/features/gdpr/placeholders/become-a-partner-search-placeholder'
import { AddressSearchConsentGate } from '@/features/google-places/address-search-consent-gate'
import { createLoadAddressHandler } from '@/features/google-places/google-places-address-helpers'
import { useGooglePlacesSelect } from '@/features/google-places/google-places-hooks'
import { GooglePlacesOption } from '@/features/google-places/google-places-option'
import {
    IGoogleAddress,
    IGoogleAddressOption,
} from '@/features/google-places/google-places-restaurants-search'
import { IGooglePlacesSelectOption } from '@/features/google-places/google-places-types'
import { Locale } from '@/setup/locales'
import { debounce } from '@/utils/functions/debounce'
import { noOp } from '@/utils/functions/noOp'
import { run } from '@/utils/run'
import { cn } from '@/utils/styles/cn'

interface IGooglePlacesRestaurantsSearchProps {
    label?: string
    placeholder?: string
    onSelect: (addressOption: IGoogleAddressOption | null) => void
    onBlur: () => void
}

const MAX_SEARCH_RESULTS = 5

export const GooglePlacesBecomeAPartnerSearch = (props: IGooglePlacesRestaurantsSearchProps) => (
    <AddressSearchConsentGate
        placeholder={(onOpen) => (
            <BecomeAPartnerSearchPlaceholder
                onOpen={onOpen}
                label={props.label}
                placeholder={props.placeholder}
            />
        )}
    >
        <BecomeAPartnerSearch {...props} />
    </AddressSearchConsentGate>
)

const BecomeAPartnerSearch = ({
    label,
    onSelect = noOp,
    placeholder,
    onBlur,
}: IGooglePlacesRestaurantsSearchProps) => {
    const locale = useLocale()

    const containerRef = useRef<HTMLDivElement>(null)

    const [listRef, { height: listHeight }] = useMeasure<HTMLDivElement>()
    const willChange = useWillChange()

    const [highlightedIndex, setHighlightedIndex] = useState<number | null>(0)
    const [isLoading, setIsLoading] = useState(false)

    const [searchValue, setSearchValue] = useState('')

    const [selectOptions, setSelectOptions] = useState<IGooglePlacesSelectOption[] | null>(null)
    const [selectedOption, setSelectedOption] = useState<IGoogleAddressOption | null>(null)

    const { loadSelectOptions, loadPlace } = useGooglePlacesSelect({ debounceWait: 250 })

    const commitId = useRef(0)

    const fetchSelectOptions = useCallback(
        (searchValue: string) => {
            loadSelectOptions(searchValue, ++commitId.current, (newOptions, loadedCommitId) => {
                // This is to avoid displaying results from a previous search
                if (loadedCommitId !== commitId.current) return

                setSelectOptions(newOptions)
                setIsLoading(false)
            })
        },
        [loadSelectOptions]
    )

    const debouncedFetchSelectOptions = useMemo(
        () => debounce((searchValue: string) => fetchSelectOptions(searchValue), 150),
        [fetchSelectOptions]
    )

    const onValueChange = useCallback(
        (searchValue: string) => {
            setSearchValue(searchValue)
            setIsLoading(true)

            setHighlightedIndex(0)

            if (!searchValue) {
                setSelectOptions(null)
                setIsLoading(false)

                return
            }

            debouncedFetchSelectOptions(searchValue)
        },
        [debouncedFetchSelectOptions]
    )

    const setAddressOption = useCallback(
        (addressOption: IGoogleAddressOption | null, _address: IGoogleAddress | null) => {
            onSelect(addressOption)
            setSelectedOption(addressOption)

            setSelectOptions(null)
            setHighlightedIndex(0)
            setSearchValue('')

            if (addressOption) {
                onBlur()
            }
        },
        [onSelect, onBlur]
    )

    const handleInputClick = useCallback(() => {
        if (selectedOption) {
            setAddressOption(null, null)

            return
        }

        if (searchValue) {
            setIsLoading(true)
            fetchSelectOptions(searchValue)
        }
    }, [selectedOption, searchValue, fetchSelectOptions, setAddressOption])

    const loadAddressOption = useMemo(
        () => createLoadAddressHandler(locale as Locale, loadPlace, setAddressOption),
        [loadPlace, locale, setAddressOption]
    )

    const handleKeyDown = useCallback(
        (event: KeyboardEvent) => {
            // Handle enter press when an option is highlighted
            if (event.key === 'Enter' && highlightedIndex !== null) {
                const selectedOption = selectOptions?.[highlightedIndex]

                if (selectedOption) loadAddressOption(selectedOption)
            }

            if (event.key === 'ArrowUp' || event.key === 'ArrowDown') {
                event.preventDefault()

                const optionsCount = Math.min(selectOptions?.length || 0, MAX_SEARCH_RESULTS)
                if (optionsCount === 0) return

                let newIndex: number | null = null

                if (highlightedIndex === null) {
                    newIndex = event.key === 'ArrowUp' ? optionsCount - 1 : 0
                } else {
                    newIndex =
                        event.key === 'ArrowUp'
                            ? (highlightedIndex - 1 + optionsCount) % optionsCount
                            : (highlightedIndex + 1) % optionsCount
                }

                setHighlightedIndex(newIndex)
            }
        },
        [selectOptions, highlightedIndex, loadAddressOption]
    )

    useEffect(() => {
        document.addEventListener('keydown', handleKeyDown)

        return () => {
            document.removeEventListener('keydown', handleKeyDown)
        }
    }, [handleKeyDown])

    return (
        <Fragment>
            <div ref={containerRef} className={cn('relative w-full md:w-auto flex')}>
                <div className="flex gap-1 items-center w-full overflow-hidden">
                    {/* autoComplete=new-password prevents the browser from showing autofill or autocomplete suggestions. */}
                    <Input
                        autoComplete="new-password"
                        isLoading={isLoading}
                        label={label}
                        value={
                            selectedOption
                                ? `${selectedOption?.value.name}, ${selectedOption?.label}`
                                : searchValue
                        }
                        onValueChange={onValueChange}
                        placeholder={placeholder}
                        inputClassName="min-h-[54px] border-[2px] truncate border-none"
                        containerClassName="w-full"
                        onClick={handleInputClick}
                        onBlur={onBlur}
                    />
                </div>

                <AnimatePresence>
                    {Boolean(selectOptions) && Boolean(searchValue) && (
                        <Fragment>
                            <div
                                onClick={() => {
                                    setSelectOptions(null)
                                }}
                                className="fixed left-0 right-0 top-0 bottom-0 min-h-dvh min-w-[100vw] z-[1]"
                            />

                            <motion.div
                                style={{ willChange }}
                                initial={{
                                    scale: 1,
                                    height: 0,
                                }}
                                animate={{
                                    scale: 1,
                                    height: listHeight || undefined,
                                }}
                                exit={{
                                    opacity: 0,
                                }}
                                transition={{
                                    opacity: { duration: 0.05, ease: 'easeInOut' },
                                    scale: { type: 'spring', stiffness: 1000, damping: 70 },
                                    height: {
                                        duration: 0.4,
                                        type: 'spring',
                                        stiffness: 400,
                                        damping: 40,
                                    },
                                }}
                                className="bg-white absolute top-[64px] rounded-l border-black overflow-hidden w-full transition-opacity
                                    shadow-custom z-[2]"
                            >
                                <motion.div ref={listRef} className="min-h-fit w-full relative">
                                    <motion.div
                                        className="w-full transition-opacity py-4"
                                        onClick={(e) => e.preventDefault()}
                                    >
                                        {run(() => {
                                            const isEmpty = !selectOptions?.length

                                            if (isEmpty)
                                                return (
                                                    <div className="flex items-center h-[73px] px-10 py-0 gap-x-4 bg-white relative w-full rounded-m">
                                                        No results found
                                                    </div>
                                                )

                                            return selectOptions
                                                ?.slice(0, MAX_SEARCH_RESULTS)
                                                .map((option, index) => {
                                                    return (
                                                        <GooglePlacesOption
                                                            highlighted={highlightedIndex === index}
                                                            key={option.value.place_id}
                                                            option={option}
                                                            onClick={() =>
                                                                loadAddressOption(option)
                                                            }
                                                            setIsLoading={setIsLoading}
                                                        />
                                                    )
                                                })
                                        })}
                                    </motion.div>
                                </motion.div>
                            </motion.div>
                        </Fragment>
                    )}
                </AnimatePresence>
            </div>
        </Fragment>
    )
}
